import React from "react";
import { DotLegalCheckbox, DotLegalDialog, DotLegalMultiSelect, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { useAddEditUserDialogHook } from "./AddEditUserDialog.hooks";
import { useAddEditUserDialogStyles } from "./AddEditUserDialog.styles";
import { Box } from "@mui/material";
import { LoginProvider, UserStartPage } from "../User.types.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import FullAccessWarningIcon from "../../common/components/fullAccessWarningIcon/FullAccessWarningIcon.tsx";

export interface IAddEditUserDialogProps {
    isEdit: boolean;
    onCloseDialog: () => void;
    onSaveCorrect: () => void;
    userId?: string;
}

function AddEditUserDialog(props: IAddEditUserDialogProps) {
    const styles = useAddEditUserDialogStyles();
    const { translateString } = useTranslation();
    const { loginProvider } = useUserContext();
    const {
        isLoading,
        viewModel,
        onChange,
        errors,
        isSaving,
        userRolesLoading,
        userRolesData,
        fullAccessRoleIds,
        businessAreasLoading,
        businessAreaData,
        groupEntitiesLoading,
        groupEntitiesData,
        permissions,
        onSave,
        useSameIntegrationIdentifierAsEmail,
        setUseSameIntegrationIdentifierAsEmail,
        startPageOptions,
        getAvaliableStartPages,
        onBusinessAreasChange,
    } = useAddEditUserDialogHook(props);

    const isAzureAdLoginProvider = loginProvider === LoginProvider.AzureAd;
    const displayIntegrationIdentifierField =
        (permissions.canChangeUserLoginProvider && props.isEdit) ||
        (!useSameIntegrationIdentifierAsEmail && isAzureAdLoginProvider) ||
        (props.isEdit && isAzureAdLoginProvider);

    return (
        <DotLegalDialog
            header={props.isEdit ? translateString("editUser") : translateString("createUser")}
            buttonOkText={props.isEdit ? translateString("save") : translateString("create")}
            open
            onDialogClose={props.onCloseDialog}
            onOkClick={onSave}
            size="sm"
            isLoading={isLoading}
            inProgress={isSaving}
            disableBackdropOnCloseIfValueChange={viewModel}
        >
            <Box sx={styles.dialogBody}>
                <DotLegalTextField
                    label={translateString("firstName")}
                    value={viewModel?.firstName!}
                    debounce={false}
                    onChange={(firstName) => {
                        let tempViewModel = { ...viewModel! };
                        tempViewModel.firstName = firstName;
                        onChange(tempViewModel);
                    }}
                    errorText={errors.firstNameError}
                />
                <DotLegalTextField
                    label={translateString("lastName")}
                    value={viewModel?.lastName!}
                    debounce={false}
                    onChange={(lastName) => {
                        let tempViewModel = { ...viewModel! };
                        tempViewModel.lastName = lastName;
                        onChange(tempViewModel);
                    }}
                    errorText={errors.lastNameError}
                />
                <DotLegalTextField
                    label={translateString("emailAddress")}
                    value={viewModel?.emailAddress!}
                    debounce={false}
                    onChange={(emailAddress) => {
                        let tempViewModel = { ...viewModel! };
                        tempViewModel.emailAddress = emailAddress;
                        onChange(tempViewModel);
                    }}
                    errorText={errors.emailError}
                />

                {!props.isEdit && isAzureAdLoginProvider && (
                    <Box sx={styles.dpoContainer}>
                        <DotLegalCheckbox
                            checked={useSameIntegrationIdentifierAsEmail}
                            onChange={(checked) => {
                                let tempViewModel = { ...viewModel! };
                                if (!checked) {
                                    tempViewModel.integrationIdentifier = "";
                                }
                                onChange(tempViewModel);
                                setUseSameIntegrationIdentifierAsEmail(checked);
                            }}
                            label={translateString("useEmailAsIntegrationIdentifier")}
                        />
                    </Box>
                )}

                {displayIntegrationIdentifierField && (
                    <Box sx={{ mt: 1 }}>
                        <DotLegalTextField
                            noMargin={!props.isEdit}
                            label={translateString("userName")}
                            value={viewModel?.integrationIdentifier!}
                            debounce={false}
                            onChange={(value) => {
                                let tempViewModel = { ...viewModel! };
                                tempViewModel.integrationIdentifier = value;
                                onChange(tempViewModel);
                            }}
                            errorText={errors.integrationIdentifierError}
                        />
                    </Box>
                )}

                <Box sx={{ marginTop: 1 }}>
                    <DotLegalMultiSelect
                        noMargin={!displayIntegrationIdentifierField && !props.isEdit && isAzureAdLoginProvider}
                        isLoading={userRolesLoading}
                        label={translateString("role")}
                        placeholder={translateString("role")}
                        options={userRolesData}
                        renderOptions={(option) => (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                {option.name}
                                {fullAccessRoleIds.has(option.id) && <FullAccessWarningIcon />}
                            </Box>
                        )}
                        chosenOptions={viewModel?.userRoles!}
                        onChange={(userRoles) => {
                            let tempViewModel = { ...viewModel! };
                            tempViewModel.userRoles = userRoles;

                            var avaliableStartPages = getAvaliableStartPages(userRoles);
                            if (tempViewModel.startPage && !avaliableStartPages.includes(tempViewModel.startPage)) {
                                tempViewModel.startPage = undefined;
                            } else if (!tempViewModel.startPage && avaliableStartPages.length > 0) {
                                tempViewModel.startPage = avaliableStartPages[0];
                            }

                            onChange(tempViewModel);
                        }}
                        noOptionsLabel={translateString("noOptions")}
                        warningText={!viewModel || viewModel?.userRoles.length === 0 ? translateString("usersWithoutRolesHasNoPlatformAccess") : undefined}
                    />
                </Box>

                <DotLegalSelect
                    options={startPageOptions}
                    onChange={(startPage) => {
                        let tempViewModel = { ...viewModel! };
                        tempViewModel.startPage = parseInt(startPage!) as UserStartPage;
                        onChange(tempViewModel);
                    }}
                    label={translateString("startPage")}
                    noOptionsLabel={translateString("noOptions")}
                    selectedItem={viewModel.startPage ? viewModel.startPage.toString() : undefined}
                    disableClearable
                    errorText={errors.startPageError}
                />

                <DotLegalMultiSelect
                    isLoading={businessAreasLoading}
                    label={translateString("businessAreas")}
                    placeholder={translateString("businessAreas")}
                    options={businessAreaData}
                    chosenOptions={viewModel?.businessAreas!}
                    onChange={onBusinessAreasChange}
                    noOptionsLabel={translateString("noOptions")}
                />

                <Box sx={styles.dpoContainer}>
                    <DotLegalCheckbox
                        label={translateString("fullGroupAccess")}
                        tooltip={translateString("fullGroupAccessToolTip")}
                        checked={viewModel?.gdpo ?? false}
                        onChange={(isGdpo) => {
                            let tempViewModel = { ...viewModel! };
                            tempViewModel.gdpo = isGdpo;
                            onChange(tempViewModel);
                        }}
                    />
                </Box>

                {!viewModel?.gdpo && (
                    <Box sx={{ mt: 1 }}>
                        <DotLegalMultiSelect
                            noMargin
                            isLoading={groupEntitiesLoading}
                            options={groupEntitiesData}
                            chosenOptions={viewModel?.groupEntityIds ?? []}
                            label={translateString("groupCompanies")}
                            noOptionsLabel={translateString("noOptions")}
                            onChange={(groupCompanies) => {
                                let tempViewModel = { ...viewModel! };
                                tempViewModel.groupEntityIds = groupCompanies;
                                onChange(tempViewModel);
                            }}
                            errorText={errors.groupEntitiesError}
                        />
                    </Box>
                )}
            </Box>
        </DotLegalDialog>
    );
}

export default AddEditUserDialog;
